import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Search, Barcode, Plus, Trash2, X, BadgeCheck, ScanLine } from "lucide-react";
import { Link } from "react-router-dom";
import { BarcodeScanner } from "@/components/BarcodeScanner";

const today = () => new Date().toISOString().slice(0, 10);
const MEALS = ["breakfast", "lunch", "dinner", "snack"];

export default function ClientNutrition() {
  const [date, setDate] = useState(today());
  const [meals, setMeals] = useState([]);
  const [q, setQ] = useState("");
  const [foods, setFoods] = useState([]);
  const [ext, setExt] = useState([]);
  const [extError, setExtError] = useState("");
  const [extLoading, setExtLoading] = useState(false);
  const [picker, setPicker] = useState(null); // {name, brand, servings}
  const [servingIdx, setServingIdx] = useState(0);
  const [qty, setQty] = useState("1");
  const [selectedMeal, setSelectedMeal] = useState("breakfast");
  const [servings, setServings] = useState("1");
  const [barcode, setBarcode] = useState("");
  const [scanning, setScanning] = useState(false);

  const load = () => api.get("/meals", { params: { date } }).then((r) => setMeals(r.data));
  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line */ }, [date]);

  useEffect(() => {
    api.get("/foods", { params: q ? { q } : {} }).then((r) => setFoods(r.data)).catch(() => {});
  }, [q]);

  useEffect(() => {
    if (q.trim().length < 2) { setExt([]); setExtError(""); return; }
    setExtLoading(true);
    const t = setTimeout(() => {
      api.get("/foods/search-external", { params: { q } })
        .then((r) => { setExt(r.data); setExtError(""); })
        .catch((err) => { setExt([]); setExtError(formatApiErrorDetail(err.response?.data?.detail) || "Verified database unavailable"); })
        .finally(() => setExtLoading(false));
    }, 500);
    return () => { clearTimeout(t); setExtLoading(false); };
  }, [q]);

  const openExternal = async (fsId) => {
    try {
      const { data } = await api.get(`/foods/external/${fsId}`);
      setPicker(data);
      setServingIdx(0);
      setQty("1");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const lookupBarcode = async (codeArg) => {
    const code = typeof codeArg === "string" ? codeArg : barcode;
    if (!code) return;
    const { data } = await api.get("/foods", { params: { barcode: code } });
    if (data.length > 0) { setFoods(data); toast.success(`Found: ${data[0].name}`); return; }
    try {
      const res = await api.get(`/foods/barcode-external/${code.trim()}`);
      setPicker(res.data);
      setServingIdx(0);
      setQty("1");
      toast.success(`Found: ${res.data.name}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No food matched that barcode.");
    }
  };

  const onScanned = (code) => {
    setScanning(false);
    setBarcode(code);
    toast.success(`Scanned: ${code}`);
    lookupBarcode(code);
  };

  const addMeal = async (food) => {
    const s = parseFloat(servings) || 1;
    try {
      await api.post("/meals", {
        date, meal_type: selectedMeal, food_id: food.food_id, name: food.name, servings: s,
        calories: food.calories * s, protein: food.protein * s, carbs: food.carbs * s, fats: food.fats * s,
      });
      toast.success("Logged");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const logExternal = async () => {
    const sv = picker.servings[servingIdx];
    if (!sv) return;
    const n = parseFloat(qty) || 1;
    try {
      await api.post("/meals", {
        date, meal_type: selectedMeal, food_id: null,
        name: picker.brand ? `${picker.name} (${picker.brand})` : picker.name,
        servings: n,
        calories: (sv.calories || 0) * n, protein: (sv.protein || 0) * n,
        carbs: (sv.carbs || 0) * n, fats: (sv.fats || 0) * n,
      });
      toast.success(`${picker.name} logged`);
      setPicker(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const totals = useMemo(() => meals.reduce((t, m) => ({
    calories: t.calories + (m.calories || 0),
    protein: t.protein + (m.protein || 0),
    carbs: t.carbs + (m.carbs || 0),
    fats: t.fats + (m.fats || 0),
  }), { calories: 0, protein: 0, carbs: 0, fats: 0 }), [meals]);

  const mealsByType = useMemo(
    () => Object.fromEntries(MEALS.map((mt) => [mt, meals.filter((m) => m.meal_type === mt)])),
    [meals]
  );

  return (
    <div className="space-y-8 lg:mt-0 mt-10" data-testid="client-nutrition">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Nutrition</div>
          <h1 className="font-serif text-5xl mt-3">Log a meal</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Link to="/app/recipes" className="btn-secondary text-xs" data-testid="use-recipe-link">Use a recipe</Link>
          <input type="date" className="input-luxe max-w-[200px]" value={date} onChange={(e) => setDate(e.target.value)} data-testid="nutrition-date" />
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { k: "calories", l: "Cal" }, { k: "protein", l: "P" }, { k: "carbs", l: "C" }, { k: "fats", l: "F" },
        ].map(({ k, l }) => (
          <div key={k} className="card-luxe" data-testid={`macro-${k}`}>
            <div className="overline">{l}</div>
            <div className="font-mono-luxe text-3xl mt-2">{Math.round(totals[k])}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-luxe space-y-4">
          <div className="overline">Add food</div>
          <div className="flex gap-2 items-center">
            <Barcode size={16} />
            <input className="input-luxe" placeholder="Barcode" value={barcode} onChange={(e) => setBarcode(e.target.value)} data-testid="barcode-input" />
            <button className="btn-secondary" onClick={() => setScanning(true)} data-testid="scan-barcode-btn"><ScanLine size={14} /> Scan</button>
            <button className="btn-secondary" onClick={() => lookupBarcode()} data-testid="barcode-lookup">Lookup</button>
          </div>
          <div className="flex gap-2 items-center">
            <Search size={16} />
            <input className="input-luxe" placeholder="Search foods…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="food-search-input" />
          </div>
          <div className="flex gap-3">
            <select className="input-luxe" value={selectedMeal} onChange={(e) => setSelectedMeal(e.target.value)} data-testid="meal-type-select">
              {MEALS.map((m) => <option key={m}>{m}</option>)}
            </select>
            <input className="input-luxe" placeholder="Servings" value={servings} onChange={(e) => setServings(e.target.value)} data-testid="servings-input" />
          </div>
          <div className="max-h-96 overflow-y-auto border-t border-luxe">
            {foods.map((f) => (
              <button key={f.food_id} onClick={() => addMeal(f)} className="w-full text-left p-3 border-b border-luxe hover:bg-surface-2 flex items-center justify-between" data-testid={`food-add-${f.food_id}`}>
                <div>
                  <div className="font-medium text-sm">{f.name}</div>
                  <div className="text-xs text-tertiary-luxe">{f.serving_size} · {Math.round(f.calories)} cal · {f.protein}P / {f.carbs}C / {f.fats}F</div>
                </div>
                <Plus size={14} className="text-accent" />
              </button>
            ))}
            {foods.length === 0 && !q && <div className="p-4 text-sm text-secondary-luxe">No foods.</div>}

            {q.trim().length >= 2 && (
              <div data-testid="verified-results">
                <div className="overline p-3 flex items-center gap-2" style={{ background: "var(--surface-2)" }}>
                  <BadgeCheck size={12} style={{ color: "var(--accent)" }} /> Verified database
                  {extLoading && <span className="text-tertiary-luxe normal-case tracking-normal">searching…</span>}
                </div>
                {extError && <div className="p-3 text-xs text-tertiary-luxe" data-testid="verified-error">{extError}</div>}
                {ext.map((f) => (
                  <button key={f.fs_id} onClick={() => openExternal(f.fs_id)}
                    className="w-full text-left p-3 border-b border-luxe hover:bg-surface-2 flex items-center justify-between"
                    data-testid={`ext-food-${f.fs_id}`}>
                    <div>
                      <div className="font-medium text-sm">{f.name}{f.brand ? <span className="text-tertiary-luxe"> · {f.brand}</span> : null}</div>
                      <div className="text-xs text-tertiary-luxe">
                        {f.serving}{f.calories != null ? ` · ${Math.round(f.calories)} cal · ${f.protein}P / ${f.carbs}C / ${f.fats}F` : ""}
                      </div>
                    </div>
                    <Plus size={14} className="text-accent" />
                  </button>
                ))}
                {!extLoading && !extError && ext.length === 0 && <div className="p-3 text-xs text-tertiary-luxe">No verified matches.</div>}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-3">
          {MEALS.map((meal) => (
            <div key={meal} className="card-luxe">
              <div className="overline">{meal}</div>
              <div className="mt-3 space-y-2">
                {(mealsByType[meal] || []).map((m) => (
                  <div key={m.meal_log_id} className="flex items-center justify-between py-2 border-b border-luxe last:border-b-0" data-testid={`logged-${m.meal_log_id}`}>
                    <div>
                      <div className="text-sm">{m.name} <span className="text-tertiary-luxe text-xs">×{m.servings}</span></div>
                      <div className="text-xs text-tertiary-luxe">{Math.round(m.calories)} cal · {Math.round(m.protein)}P / {Math.round(m.carbs)}C / {Math.round(m.fats)}F</div>
                    </div>
                    <button className="btn-ghost" onClick={async () => { await api.delete(`/meals/${m.meal_log_id}`); load(); }}><Trash2 size={14}/></button>
                  </div>
                ))}
                {(mealsByType[meal] || []).length === 0 && <div className="text-xs text-tertiary-luxe">Empty.</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {scanning && <BarcodeScanner onDetected={onScanned} onClose={() => setScanning(false)} />}

      {picker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(10,9,8,0.72)" }}
          onClick={() => setPicker(null)} data-testid="serving-picker">
          <div className="card-luxe max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <div className="overline flex items-center gap-2"><BadgeCheck size={12} style={{ color: "var(--accent)" }} /> Verified food</div>
                <h2 className="font-serif text-2xl mt-2">{picker.name}</h2>
                {picker.brand && <div className="text-xs text-tertiary-luxe mt-1">{picker.brand}</div>}
              </div>
              <button className="btn-ghost" onClick={() => setPicker(null)} data-testid="close-serving-picker"><X size={15} /></button>
            </div>
            <div className="mt-5 space-y-4">
              <div>
                <label className="label-luxe">Serving</label>
                <select className="input-luxe" value={servingIdx} onChange={(e) => setServingIdx(parseInt(e.target.value, 10))} data-testid="serving-select">
                  {picker.servings.map((s, i) => (
                    <option key={s.serving_id || i} value={i}>{s.description} — {Math.round(s.calories)} cal</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-luxe">Quantity</label>
                  <input className="input-luxe" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="serving-qty" />
                </div>
                <div>
                  <label className="label-luxe">Meal</label>
                  <select className="input-luxe" value={selectedMeal} onChange={(e) => setSelectedMeal(e.target.value)}>
                    {MEALS.map((m) => <option key={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              {picker.servings[servingIdx] && (
                <div className="grid grid-cols-4 gap-px" style={{ background: "var(--border-strong)" }}>
                  {[["cal", "calories"], ["P", "protein"], ["C", "carbs"], ["F", "fats"]].map(([l, k]) => (
                    <div key={k} className="p-3 text-center" style={{ background: "var(--surface-2)" }}>
                      <div className="font-mono-luxe text-base">{Math.round((picker.servings[servingIdx][k] || 0) * (parseFloat(qty) || 1))}</div>
                      <div className="overline mt-1">{l}</div>
                    </div>
                  ))}
                </div>
              )}
              <button className="btn-primary w-full justify-center" onClick={logExternal} data-testid="log-external-btn">Log this food</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
